import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  XCircle,
  Loader2,
  Building2,
  Users,
  AlertCircle,
  Info,
  Eye,
  EyeOff,
  Download,
  FileDown,
} from 'lucide-react';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'closed_won', label: 'Closed Won' },
  { value: 'closed_lost', label: 'Closed Lost' },
];

const LEAD_TYPE_OPTIONS = [
  { value: 'staffing_company', label: 'Staffing Company' },
  { value: 'client', label: 'Client' },
  { value: 'investor', label: 'Investor' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'franchise', label: 'Franchise' },
  { value: 'misc', label: 'Misc' },
];

const COMPANY_FIELDS = [
  { key: 'name',             label: 'Company Name',    required: true, example: 'Acme Corp' },
  { key: 'website',          label: 'Website URL',                     example: 'https://acme.com' },
  { key: 'company_type',     label: 'Company Type',    hint: 'potential, client, pot-comp, vendor, others', example: 'potential' },
  { key: 'industry',         label: 'Industry',                        example: 'Technology' },
  { key: 'address1',         label: 'Address Line 1',                  example: '123 Main St' },
  { key: 'address2',         label: 'Address Line 2',                  example: 'Suite 100' },
  { key: 'city',             label: 'City',                            example: 'New York' },
  { key: 'state',            label: 'State',                           example: 'NY' },
  { key: 'zip',              label: 'ZIP Code',                        example: '10001' },
  { key: 'num_recruiters',   label: 'No. of Recruiters', hint: 'Number',  example: '10' },
  { key: 'num_reqs_open',    label: 'Open Reqs',          hint: 'Number',  example: '25' },
  { key: 'years_in_business',label: 'Years in Business',  hint: 'Number',  example: '5' },
  { key: 'geographies',      label: 'Geographies',                     example: 'Northeast, Southeast' },
  { key: 'revenues',         label: 'Revenues',        hint: 'Annual revenue in dollars', example: '5000000' },
  { key: 'ats_used',         label: 'ATS Used',                        example: 'Greenhouse' },
  { key: 'company_notes',    label: 'Company Notes',                   example: 'Key account' },
];

const LEAD_FIELDS = [
  { key: 'first_name',         label: 'First Name',         required: true, example: 'John' },
  { key: 'last_name',          label: 'Last Name',                          example: 'Smith' },
  { key: 'prefix',             label: 'Prefix',                             example: 'Mr.' },
  { key: 'gender',             label: 'Gender',             hint: 'male, female, other', example: 'male' },
  { key: 'title',              label: 'Title',                              example: 'VP of Sales' },
  { key: 'company_name',       label: 'Company Name',                       example: 'Acme Corp' },
  { key: 'work_email',         label: 'Work Email',                         example: 'john@acme.com' },
  { key: 'personal_email',     label: 'Personal Email',                     example: 'john@gmail.com' },
  { key: 'work_direct_phone',  label: 'Work Direct Phone',                  example: '+15551234567' },
  { key: 'work_company_phone', label: 'Work Company Phone',                 example: '+15559876543' },
  { key: 'mobile_phone',       label: 'Mobile Phone',                       example: '+15559876543' },
  { key: 'linkedin_url',       label: 'LinkedIn URL',                       example: 'https://linkedin.com/in/johnsmith' },
  { key: 'linkedin_url_x',     label: 'LinkedIn URL-X',    hint: 'Secondary LinkedIn URL', example: '' },
  { key: 'x_twitter_url',      label: 'X/Twitter URL',                      example: 'https://twitter.com/johnsmith' },
  { key: 'location',           label: 'Office Location',   hint: 'Company office location name (e.g. HQ, Chicago Office)', example: 'HQ' },
  { key: 'source',             label: 'Source',                             example: 'Apollo' },
  { key: 'timezone',           label: 'Timezone',                           example: 'America/New_York' },
  { key: 'batch_name',         label: 'Batch Name',        hint: 'Tag for grouping this import', example: 'Q1-2026' },
  { key: 'status',             label: 'Status',            hint: 'new, contacted, qualified, closed_won, closed_lost', example: 'new' },
  { key: 'lead_types',         label: 'Lead Types',        hint: 'Semicolon-separated',    example: 'staffing_company' },
  { key: 'dnc',                label: 'DNC',               hint: 'Do Not Call: true or false', example: 'false' },
  { key: 'dne',                label: 'DNE',               hint: 'Do Not Email: true or false', example: 'false' },
  { key: 'description',        label: 'Notes',                              example: 'Met at conference' },
  { key: 'owner',              label: 'Owner',             hint: 'Owner name from CSV (used when "From CSV column" is selected)', example: 'John Smith' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseFile(file, onSuccess, onError) {
  const ext = file.name.split('.').pop().toLowerCase();
  if (ext === 'xlsx' || ext === 'xls') {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type: 'binary' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '' });
        const headers = rows.length > 0 ? Object.keys(rows[0]) : [];
        onSuccess({ headers, rows });
      } catch (err) {
        onError(err);
      }
    };
    reader.onerror = () => onError(new Error('Failed to read file'));
    reader.readAsBinaryString(file);
  } else if (ext === 'csv') {
    const reader = new FileReader();
    reader.onload = (e) => {
      Papa.parse(e.target.result, {
        header: true,
        skipEmptyLines: 'greedy',
        complete: (results) => {
          if (!results.data?.length) { onError(new Error('CSV file appears to be empty')); return; }
          onSuccess({ headers: results.meta.fields || [], rows: results.data });
        },
        error: onError,
      });
    };
    reader.onerror = () => onError(new Error('Failed to read file'));
    reader.readAsText(file);
  } else {
    onError(new Error('Unsupported format. Please use CSV, XLSX, or XLS.'));
  }
}

function autoMap(headers, fields) {
  const mappings = {};
  fields.forEach((field) => {
    const match = headers.find((h) => {
      const hl = h.toLowerCase().trim();
      const fk = field.key.toLowerCase();
      const fl = field.label.toLowerCase();
      if (hl === fl || hl === fk) return true;
      if (fk === 'name'              && (hl === 'company' || hl === 'company name' || hl === 'companyname')) return true;
      if (fk === 'company_name'      && (hl === 'company' || hl === 'companyname')) return true;
      if (fk === 'first_name'        && (hl === 'firstname' || hl === 'first' || hl === 'title' || hl === 'name')) return true;
      if (fk === 'last_name'         && (hl === 'lastname' || hl === 'last')) return true;
      if (fk === 'title'             && (hl === 'job title' || hl === 'jobtitle' || hl === 'job_title' || hl === 'position')) return true;
      if (fk === 'work_email'        && (hl === 'email' || hl === 'workemail' || hl === 'emailw')) return true;
      if (fk === 'linkedin_url'      && (hl === 'linkedin' || hl === 'leadlinkedinurl' || hl === 'normalizedurl')) return true;
      if (fk === 'x_twitter_url'     && (hl === 'twitter' || hl === 'x' || hl === 'twitter_url' || hl === 'x_url')) return true;
      if (fk === 'work_direct_phone' && (hl === 'phone' || hl === 'phonew' || hl === 'work_phone')) return true;
      if (fk === 'mobile_phone'      && (hl === 'mobile' || hl === 'phonem' || hl === 'cell')) return true;
      return false;
    });
    if (match) mappings[field.key] = match;
  });
  return mappings;
}

function downloadTemplate(fields, filename) {
  const headers = fields.map((f) => f.label);
  const examples = fields.map((f) => f.example || '');
  const csv = Papa.unparse([headers, examples], { header: false });
  triggerDownload(csv, filename, 'text/csv');
}

function downloadFailedRows(csvData, errors) {
  if (!errors?.length || !csvData) return;
  const rows = errors.map((e) => {
    const rowIdx = (e.row || 1) - 1;
    return { ...(csvData.rows[rowIdx] || {}), '__Error__': e.error };
  });
  const csv = Papa.unparse(rows);
  triggerDownload(csv, 'failed_rows.csv', 'text/csv');
}

function triggerDownload(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function DropZone({ onFile, isDragging, setIsDragging, file }) {
  const inputRef = useRef(null);
  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all select-none ${
        isDragging
          ? 'border-indigo-500 bg-indigo-50 scale-[1.01]'
          : file
          ? 'border-green-400 bg-green-50'
          : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'
      }`}
      onDragOver={stop}
      onDragEnter={(e) => { stop(e); setIsDragging(true); }}
      onDragLeave={(e) => { stop(e); setIsDragging(false); }}
      onDrop={(e) => { stop(e); setIsDragging(false); const f = e.dataTransfer.files[0]; if (f) onFile(f); }}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        className="hidden"
        onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); }}
      />
      {file ? (
        <div className="flex items-center justify-center gap-3">
          <FileSpreadsheet className="h-10 w-10 text-green-600 flex-shrink-0" />
          <div className="text-left">
            <p className="font-semibold text-green-700 truncate max-w-xs">{file.name}</p>
            <p className="text-sm text-green-600 mt-0.5">Click or drop to replace</p>
          </div>
          <CheckCircle className="h-5 w-5 text-green-500 ml-auto flex-shrink-0" />
        </div>
      ) : (
        <>
          <Upload className={`h-10 w-10 mx-auto mb-3 ${isDragging ? 'text-indigo-500' : 'text-slate-400'}`} />
          <p className="font-semibold text-slate-700">Drop your file here</p>
          <p className="text-sm text-slate-400 mt-1">or click to browse</p>
          <div className="flex items-center justify-center gap-2 mt-3">
            <Badge variant="outline" className="text-xs font-mono">CSV</Badge>
            <Badge variant="outline" className="text-xs font-mono">XLSX</Badge>
            <Badge variant="outline" className="text-xs font-mono">XLS</Badge>
          </div>
        </>
      )}
    </div>
  );
}

function RawPreviewTable({ data, maxRows = 5 }) {
  if (!data) return null;
  const preview = data.rows.slice(0, maxRows);
  return (
    <div className="rounded-lg border overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50 border-b">
        <Eye className="h-3.5 w-3.5 text-slate-500 flex-shrink-0" />
        <span className="text-xs font-medium text-slate-600">
          File preview — first {preview.length} of <strong>{data.rows.length}</strong> rows · <strong>{data.headers.length}</strong> columns detected
        </span>
      </div>
      <div className="overflow-auto max-h-44">
        <Table>
          <TableHeader>
            <TableRow>
              {data.headers.map((h) => (
                <TableHead key={h} className="text-xs whitespace-nowrap px-2 py-1.5 bg-slate-50/80 font-semibold">{h}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {preview.map((row, i) => (
              <TableRow key={i} className={i % 2 === 1 ? 'bg-slate-50/50' : ''}>
                {data.headers.map((h) => (
                  <TableCell key={h} className="text-xs px-2 py-1 max-w-[180px] truncate whitespace-nowrap text-slate-600">
                    {String(row[h] ?? '') || <span className="text-slate-300">—</span>}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function MappedPreviewTable({ csvData, mappings, staticValues, fields, maxRows = 12 }) {
  const activeCols = fields.filter((f) => mappings[f.key] || staticValues[f.key]);
  if (activeCols.length === 0) {
    return (
      <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0" />
        <p className="text-sm text-amber-700">No fields mapped yet. Map at least one column above to see the preview.</p>
      </div>
    );
  }
  const preview = csvData.rows.slice(0, maxRows);

  return (
    <div className="rounded-lg border border-indigo-200 overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border-b border-indigo-200">
        <Eye className="h-3.5 w-3.5 text-indigo-600 flex-shrink-0" />
        <span className="text-xs font-medium text-indigo-700">
          Import preview — first {preview.length} of <strong>{csvData.rows.length}</strong> rows · <strong>{activeCols.length}</strong> mapped fields
        </span>
      </div>
      <div className="overflow-auto max-h-64">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs px-2 py-1.5 bg-indigo-50 text-indigo-500 w-8">#</TableHead>
              {activeCols.map((f) => (
                <TableHead key={f.key} className="text-xs px-2 py-1.5 bg-indigo-50 text-indigo-700 whitespace-nowrap">
                  {f.label}{f.required && <span className="text-red-500 ml-0.5">*</span>}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {preview.map((row, i) => (
              <TableRow key={i} className={i % 2 === 1 ? 'bg-slate-50/40' : ''}>
                <TableCell className="text-xs px-2 py-1 text-slate-400">{i + 1}</TableCell>
                {activeCols.map((f) => {
                  const val = staticValues[f.key]
                    ? String(staticValues[f.key])
                    : mappings[f.key]
                    ? String(row[mappings[f.key]] ?? '')
                    : '';
                  const missing = !val && f.required;
                  return (
                    <TableCell
                      key={f.key}
                      className={`text-xs px-2 py-1 max-w-[180px] truncate whitespace-nowrap ${
                        missing ? 'text-red-400 bg-red-50' : val ? 'text-slate-700' : 'text-slate-300'
                      }`}
                    >
                      {val || (missing ? '⚠ Missing' : '—')}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {csvData.rows.length > maxRows && (
        <div className="px-3 py-1.5 bg-slate-50 border-t text-xs text-slate-400">
          + {csvData.rows.length - maxRows} more rows not shown
        </div>
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function CSVImportWizard({ open, onOpenChange, onImportComplete, companyOnly = false }) {
  const { user: currentUser } = useAuth();
  const [step, setStep] = useState(1);
  const [skipCompanies, setSkipCompanies] = useState(false);

  // Companies state
  const [companyFile, setCompanyFile] = useState(null);
  const [companyCsvData, setCompanyCsvData] = useState(null);
  const [companyMappings, setCompanyMappings] = useState({});
  const [companyStaticValues, setCompanyStaticValues] = useState({});
  const [importingCompanies, setImportingCompanies] = useState(false);
  const [companyResults, setCompanyResults] = useState(null);
  const [isDraggingCompany, setIsDraggingCompany] = useState(false);
  const [showCompanyPreview, setShowCompanyPreview] = useState(false);

  // Leads state
  const [leadFile, setLeadFile] = useState(null);
  const [leadCsvData, setLeadCsvData] = useState(null);
  const [leadMappings, setLeadMappings] = useState({});
  const [leadStaticValues, setLeadStaticValues] = useState({});
  const [importingLeads, setImportingLeads] = useState(false);
  const [leadResults, setLeadResults] = useState(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [duplicateCheckField, setDuplicateCheckField] = useState('work_email');
  const [isDraggingLead, setIsDraggingLead] = useState(false);
  const [showLeadPreview, setShowLeadPreview] = useState(false);

  // Owner assignment
  const [ownerOption, setOwnerOption] = useState('me'); // 'me' | 'none' | 'user' | 'csv'
  const [ownerUserId, setOwnerUserId] = useState('');
  const [ownerUserName, setOwnerUserName] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const handleClose = () => {
    setStep(1);
    setSkipCompanies(false);
    setCompanyFile(null); setCompanyCsvData(null); setCompanyMappings({}); setCompanyStaticValues({}); setCompanyResults(null);
    setLeadFile(null); setLeadCsvData(null); setLeadMappings({}); setLeadStaticValues({}); setLeadResults(null);
    setShowCompanyPreview(false); setShowLeadPreview(false);
    setOwnerOption('me'); setOwnerUserId(''); setOwnerUserName('');
    onOpenChange(false);
  };

  // Fetch users when wizard opens
  useEffect(() => {
    if (!open || allUsers.length > 0) return;
    setLoadingUsers(true);
    axios.get(`${API}/users/active-basic`).then(res => {
      const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
      setAllUsers(list);
    }).catch(() => {}).finally(() => setLoadingUsers(false));
  }, [open]);

  const handleCompanyFile = (file) => {
    parseFile(
      file,
      ({ headers, rows }) => {
        setCompanyFile(file);
        setCompanyCsvData({ headers, rows });
        setCompanyMappings(autoMap(headers, COMPANY_FIELDS));
        setCompanyStaticValues({});
        setShowCompanyPreview(false);
        toast.success(`Loaded ${rows.length} companies (${headers.length} columns)`);
      },
      (err) => toast.error(err.message || 'Failed to parse file'),
    );
  };

  const handleLeadFile = (file) => {
    parseFile(
      file,
      ({ headers, rows }) => {
        setLeadFile(file);
        setLeadCsvData({ headers, rows });
        setLeadMappings(autoMap(headers, LEAD_FIELDS));
        setLeadStaticValues({});
        setShowLeadPreview(false);
        toast.success(`Loaded ${rows.length} leads (${headers.length} columns)`);
      },
      (err) => toast.error(err.message || 'Failed to parse file'),
    );
  };

  const handleImportCompanies = async () => {
    if (!companyCsvData) { toast.error('Please upload a companies file'); return; }
    if (!companyMappings['name'] && !companyStaticValues.name) {
      toast.error('Company Name is required. Please map it or provide a static value.'); return;
    }
    setImportingCompanies(true);
    try {
      const importData = companyCsvData.rows.map((row) => {
        const mapped = {};
        Object.entries(companyMappings).forEach(([k, col]) => { if (col && col !== 'skip') mapped[k] = row[col] || ''; });
        Object.entries(companyStaticValues).forEach(([k, v]) => { if (v) mapped[k] = v; });
        return mapped;
      });
      const res = await axios.post(`${API}/admin/import-companies`, { companies: importData, skip_duplicates: true });
      setCompanyResults(res.data);
      toast.success(`Imported ${res.data.imported} companies`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import companies');
    } finally {
      setImportingCompanies(false);
    }
  };

  const handleImportLeads = async () => {
    if (!leadCsvData) { toast.error('Please upload a leads file'); return; }
    if (!leadMappings['first_name'] && !leadStaticValues.first_name) {
      toast.error('First Name is required. Please map it or provide a static value.'); return;
    }
    setImportingLeads(true);
    try {
      const importData = leadCsvData.rows.map((row) => {
        const mapped = {};
        Object.entries(leadMappings).forEach(([k, col]) => { if (col && col !== 'skip') mapped[k] = row[col] || ''; });
        Object.entries(leadStaticValues).forEach(([k, v]) => { if (v) mapped[k] = v; });
        if (mapped.first_name?.includes(' ') && !mapped.last_name) {
          const parts = mapped.first_name.trim().split(' ');
          mapped.first_name = parts[0];
          mapped.last_name = parts.slice(1).join(' ');
        }
        return mapped;
      });
      const res = await axios.post(`${API}/import/leads`, {
        leads_data: importData,
        skip_duplicates: skipDuplicates,
        duplicate_check_field: duplicateCheckField,
        owner_option: ownerOption,
        default_owner_id: ownerOption === 'user' ? ownerUserId : null,
        default_owner_name: ownerOption === 'user' ? ownerUserName : null,
      });
      setLeadResults(res.data);
      toast.success(`Imported ${res.data.imported} leads`);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to import leads');
    } finally {
      setImportingLeads(false);
    }
  };

  const updateCompanyMapping = (fieldKey, value) => {
    setCompanyMappings((m) => ({ ...m, [fieldKey]: value === 'skip' ? '' : value }));
    if (value !== 'skip') setCompanyStaticValues((s) => { const n = { ...s }; delete n[fieldKey]; return n; });
  };

  const updateCompanyStatic = (fieldKey, value) => {
    setCompanyStaticValues((s) => ({ ...s, [fieldKey]: value }));
    if (value) setCompanyMappings((m) => { const n = { ...m }; delete n[fieldKey]; return n; });
  };

  const updateLeadMapping = (fieldKey, value) => {
    setLeadMappings((m) => ({ ...m, [fieldKey]: value === 'skip' ? '' : value }));
    if (value !== 'skip') setLeadStaticValues((s) => { const n = { ...s }; delete n[fieldKey]; return n; });
  };

  const updateLeadStatic = (fieldKey, value) => {
    setLeadStaticValues((s) => ({ ...s, [fieldKey]: value }));
    if (value) setLeadMappings((m) => { const n = { ...m }; delete n[fieldKey]; return n; });
  };

  const mappedCompanyCount = Object.values(companyMappings).filter(Boolean).length + Object.values(companyStaticValues).filter(Boolean).length;
  const mappedLeadCount = Object.values(leadMappings).filter(Boolean).length + Object.values(leadStaticValues).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            Import Wizard
            <Badge variant="outline" className="ml-auto text-xs">{companyOnly ? 'Companies Import' : `Step ${step} of 2`}</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* ── Step 1: Companies ─────────────────────────────────── */}
        {step === 1 && (
          <div className="space-y-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 className="h-4 w-4" />
                  Step 1: Companies
                </CardTitle>
                <CardDescription>
                  Import company records first, or skip if companies are already in the system.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!companyOnly && (
                  <div className="flex items-center space-x-2">
                    <Checkbox id="skip-co" checked={skipCompanies} onCheckedChange={setSkipCompanies} />
                    <Label htmlFor="skip-co" className="cursor-pointer text-sm">
                      Skip — companies already exist (or will be auto-created during lead import)
                    </Label>
                  </div>
                )}

                {!skipCompanies && (
                  <div className="space-y-4">
                    {/* Drop zone */}
                    <DropZone
                      onFile={handleCompanyFile}
                      isDragging={isDraggingCompany}
                      setIsDragging={setIsDraggingCompany}
                      file={companyFile}
                    />

                    {/* Template download */}
                    <div className="flex items-center justify-between">
                      <Button variant="ghost" size="sm" onClick={() => downloadTemplate(COMPANY_FIELDS, 'companies_template.csv')}>
                        <FileDown className="h-3.5 w-3.5 mr-1.5" />
                        Download Template CSV
                      </Button>
                      {companyCsvData && (
                        <Badge variant="secondary">{companyCsvData.rows.length} rows · {companyCsvData.headers.length} columns</Badge>
                      )}
                    </div>

                    {/* Raw preview */}
                    {companyCsvData && <RawPreviewTable data={companyCsvData} />}

                    {/* Mapping table */}
                    {companyCsvData && (
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className="text-sm font-semibold text-slate-700">Column Mapping</h3>
                          <span className="text-xs text-slate-400">{mappedCompanyCount} field{mappedCompanyCount !== 1 ? 's' : ''} mapped</span>
                        </div>

                        <div className="rounded-lg border overflow-hidden">
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-slate-50">
                                <TableHead className="text-xs w-[180px]">System Field</TableHead>
                                <TableHead className="text-xs w-[180px]">CSV Column</TableHead>
                                <TableHead className="text-xs w-[180px]">OR Static Value</TableHead>
                                <TableHead className="text-xs">Sample</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {COMPANY_FIELDS.map((field) => (
                                <TableRow key={field.key}>
                                  <TableCell className="text-xs font-medium py-2">
                                    {field.label} {field.required && <span className="text-red-500">*</span>}
                                    {field.hint && <p className="text-[10px] text-slate-400 font-normal">{field.hint}</p>}
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <Select value={companyMappings[field.key] || 'skip'} onValueChange={(v) => updateCompanyMapping(field.key, v)}>
                                      <SelectTrigger className="h-8 text-xs">
                                        <SelectValue placeholder="Skip" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="skip">Skip</SelectItem>
                                        {companyCsvData.headers.map((h) => (
                                          <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </TableCell>
                                  <TableCell className="py-2">
                                    <Input
                                      placeholder="Static value…"
                                      value={companyStaticValues[field.key] || ''}
                                      onChange={(e) => updateCompanyStatic(field.key, e.target.value)}
                                      className="h-8 text-xs"
                                    />
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-500 py-2 max-w-[160px] truncate">
                                    {companyStaticValues[field.key] ? (
                                      <span className="text-green-600 font-medium">"{companyStaticValues[field.key]}"</span>
                                    ) : companyMappings[field.key] && companyCsvData.rows[0]?.[companyMappings[field.key]] ? (
                                      String(companyCsvData.rows[0][companyMappings[field.key]]).substring(0, 35)
                                    ) : (
                                      <span className="text-slate-300">—</span>
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {/* Import preview toggle */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowCompanyPreview((v) => !v)}
                          className="w-full"
                        >
                          {showCompanyPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                          {showCompanyPreview ? 'Hide Import Preview' : 'Show Import Preview'}
                        </Button>

                        {showCompanyPreview && (
                          <MappedPreviewTable
                            csvData={companyCsvData}
                            mappings={companyMappings}
                            staticValues={companyStaticValues}
                            fields={COMPANY_FIELDS}
                          />
                        )}

                        <Button onClick={handleImportCompanies} disabled={importingCompanies} className="w-full">
                          {importingCompanies ? (
                            <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing Companies…</>
                          ) : (
                            <><CheckCircle className="mr-2 h-4 w-4" />Import {companyCsvData.rows.length} Companies</>
                          )}
                        </Button>

                        {companyResults && (
                          <Card className={companyResults.errors?.length ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}>
                            <CardContent className="pt-4 pb-4">
                              <div className="flex items-start gap-3">
                                {companyResults.errors?.length ? (
                                  <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                                ) : (
                                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                )}
                                <div className="flex-1 space-y-1 text-sm">
                                  <p className="font-semibold">Companies Import Complete</p>
                                  <p>✓ Imported: <strong>{companyResults.imported}</strong></p>
                                  <p>↷ Duplicates skipped: <strong>{companyResults.duplicates || 0}</strong></p>
                                  {companyResults.errors?.length > 0 && (
                                    <div>
                                      <p className="text-red-600 font-medium">✗ Errors: {companyResults.errors.length}</p>
                                      <div className="mt-1 p-2 bg-white border border-red-200 rounded max-h-32 overflow-y-auto">
                                        {companyResults.errors.map((e, i) => (
                                          <p key={i} className="text-xs text-red-700">Row {e.row}: {e.error}</p>
                                        ))}
                                      </div>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-2 text-xs"
                                        onClick={() => downloadFailedRows(companyCsvData, companyResults.errors)}
                                      >
                                        <Download className="h-3 w-3 mr-1.5" />
                                        Download Failed Rows
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex justify-end gap-3 pt-3 border-t">
                  <Button variant="outline" onClick={handleClose}>Cancel</Button>
                  {companyOnly ? (
                    <Button onClick={handleClose} disabled={!companyResults}>
                      Finish
                    </Button>
                  ) : (
                    <Button
                      onClick={() => {
                        if (!skipCompanies && !companyResults) {
                          toast.error('Import companies first, or check "Skip"');
                          return;
                        }
                        setStep(2);
                      }}
                    >
                      Next: Import Leads <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* ── Step 2: Leads ─────────────────────────────────────── */}
        {step === 2 && (
          <div className="space-y-5">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Users className="h-4 w-4" />
                  Step 2: Leads
                </CardTitle>
                <CardDescription>
                  Upload your leads file. Missing companies will be auto-created.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">

                {/* Drop zone */}
                <DropZone
                  onFile={handleLeadFile}
                  isDragging={isDraggingLead}
                  setIsDragging={setIsDraggingLead}
                  file={leadFile}
                />

                {/* Template download */}
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="sm" onClick={() => downloadTemplate(LEAD_FIELDS, 'leads_template.csv')}>
                    <FileDown className="h-3.5 w-3.5 mr-1.5" />
                    Download Template CSV
                  </Button>
                  {leadCsvData && (
                    <Badge variant="secondary">{leadCsvData.rows.length} rows · {leadCsvData.headers.length} columns</Badge>
                  )}
                </div>

                {/* Full-name split hint */}
                {leadCsvData && (
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Info className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-800">
                      <strong>Full Name Split:</strong> If your file has full names in one column (e.g. "John Smith"), map it to <strong>First Name</strong>.
                      The system will auto-split it into first and last names.
                    </p>
                  </div>
                )}

                {/* Raw preview */}
                {leadCsvData && <RawPreviewTable data={leadCsvData} />}

                {leadCsvData && (
                  <div className="space-y-3">
                    {/* Duplicate detection */}
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                      <Label className="text-xs font-semibold text-blue-900">Duplicate Detection</Label>
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-2">
                          <Checkbox id="skip-dup" checked={skipDuplicates} onCheckedChange={setSkipDuplicates} />
                          <Label htmlFor="skip-dup" className="text-xs cursor-pointer">Skip duplicates</Label>
                        </div>
                        {skipDuplicates && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-600">Check by:</span>
                            <Select value={duplicateCheckField} onValueChange={setDuplicateCheckField}>
                              <SelectTrigger className="h-7 w-[160px] text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="work_email">Work Email</SelectItem>
                                <SelectItem value="personal_email">Personal Email</SelectItem>
                                <SelectItem value="linkedin_url">LinkedIn URL</SelectItem>
                                <SelectItem value="work_direct_phone">Work Phone</SelectItem>
                                <SelectItem value="mobile_phone">Mobile Phone</SelectItem>
                                <SelectItem value="full_name_company">Name + Company</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Owner Assignment */}
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
                      <Label className="text-xs font-semibold text-amber-900">Lead Ownership</Label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'me',   label: 'Assign to Me' },
                          { value: 'none', label: 'No Owner' },
                          { value: 'user', label: 'Choose User' },
                          { value: 'csv',  label: 'From CSV Column' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setOwnerOption(opt.value)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${ownerOption === opt.value ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-800 border-amber-300 hover:bg-amber-100'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      {ownerOption === 'me' && currentUser && (
                        <p className="text-xs text-amber-700">All leads will be assigned to <strong>{currentUser.full_name || currentUser.email}</strong></p>
                      )}
                      {ownerOption === 'none' && (
                        <p className="text-xs text-amber-700">Leads will be imported with no owner (can be assigned later)</p>
                      )}
                      {ownerOption === 'user' && (
                        <div className="mt-1">
                          <Select
                            value={ownerUserId}
                            onValueChange={(v) => {
                              setOwnerUserId(v);
                              setOwnerUserName(allUsers.find(u => u.id === v)?.full_name || '');
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs bg-white">
                              <SelectValue placeholder={loadingUsers ? 'Loading users…' : 'Select a user…'} />
                            </SelectTrigger>
                            <SelectContent>
                              {allUsers.map(u => (
                                <SelectItem key={u.id} value={u.id}>{u.full_name || u.email}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      {ownerOption === 'csv' && (
                        <p className="text-xs text-amber-700">Map the <strong>"Owner"</strong> column below to a CSV column containing owner names</p>
                      )}
                    </div>

                    {/* Mapping table */}
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-semibold text-slate-700">Column Mapping</h3>
                      <span className="text-xs text-slate-400">{mappedLeadCount} field{mappedLeadCount !== 1 ? 's' : ''} mapped</span>
                    </div>

                    <div className="rounded-lg border overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs w-[180px]">System Field</TableHead>
                            <TableHead className="text-xs w-[180px]">CSV Column</TableHead>
                            <TableHead className="text-xs w-[180px]">OR Static Value</TableHead>
                            <TableHead className="text-xs">Sample</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {LEAD_FIELDS.map((field) => (
                            <TableRow key={field.key}>
                              <TableCell className="text-xs font-medium py-2">
                                {field.label} {field.required && <span className="text-red-500">*</span>}
                                {field.hint && <p className="text-[10px] text-slate-400 font-normal">{field.hint}</p>}
                              </TableCell>
                              <TableCell className="py-2">
                                <Select value={leadMappings[field.key] || 'skip'} onValueChange={(v) => updateLeadMapping(field.key, v)}>
                                  <SelectTrigger className="h-8 text-xs">
                                    <SelectValue placeholder="Skip" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="skip">Skip</SelectItem>
                                    {leadCsvData.headers.map((h) => (
                                      <SelectItem key={h} value={h} className="text-xs">{h}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </TableCell>
                              <TableCell className="py-2">
                                {field.key === 'status' ? (
                                  <Select
                                    value={leadStaticValues[field.key] || 'none'}
                                    onValueChange={(v) => updateLeadStatic(field.key, v === 'none' ? '' : v)}
                                  >
                                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No default" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">No default</SelectItem>
                                      {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : field.key === 'lead_types' ? (
                                  <Select
                                    value={leadStaticValues[field.key] || 'none'}
                                    onValueChange={(v) => updateLeadStatic(field.key, v === 'none' ? '' : v)}
                                  >
                                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="No default" /></SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="none">No default</SelectItem>
                                      {LEAD_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                ) : (
                                  <Input
                                    placeholder="Static value…"
                                    value={leadStaticValues[field.key] || ''}
                                    onChange={(e) => updateLeadStatic(field.key, e.target.value)}
                                    className="h-8 text-xs"
                                  />
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500 py-2 max-w-[160px] truncate">
                                {leadStaticValues[field.key] ? (
                                  <span className="text-green-600 font-medium">"{leadStaticValues[field.key]}"</span>
                                ) : leadMappings[field.key] && leadCsvData.rows[0]?.[leadMappings[field.key]] ? (
                                  String(leadCsvData.rows[0][leadMappings[field.key]]).substring(0, 35)
                                ) : (
                                  <span className="text-slate-300">—</span>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {/* Import preview toggle */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLeadPreview((v) => !v)}
                      className="w-full"
                    >
                      {showLeadPreview ? <EyeOff className="h-4 w-4 mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
                      {showLeadPreview ? 'Hide Import Preview' : 'Show Import Preview'}
                    </Button>

                    {showLeadPreview && (
                      <MappedPreviewTable
                        csvData={leadCsvData}
                        mappings={leadMappings}
                        staticValues={leadStaticValues}
                        fields={LEAD_FIELDS}
                      />
                    )}

                    {/* Import button */}
                    <Button onClick={handleImportLeads} disabled={importingLeads} className="w-full">
                      {importingLeads ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing Leads…</>
                      ) : (
                        <><CheckCircle className="mr-2 h-4 w-4" />Import {leadCsvData.rows.length} Leads</>
                      )}
                    </Button>

                    {/* Results */}
                    {leadResults && (
                      <Card className={leadResults.errors?.length ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}>
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-start gap-3">
                            {leadResults.errors?.length ? (
                              <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                            ) : (
                              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                            )}
                            <div className="flex-1 space-y-1 text-sm">
                              <p className="font-semibold">
                                {leadResults.imported > 0 ? `Leads Import Complete 🎉` : 'Import Finished'}
                              </p>
                              <div className="grid grid-cols-2 gap-x-6 gap-y-0.5 text-xs">
                                <p>✓ Leads imported: <strong>{leadResults.imported}</strong></p>
                                <p>↷ Duplicates skipped: <strong>{leadResults.duplicates || 0}</strong></p>
                                <p>🏢 Companies created: <strong>{leadResults.companies_created || 0}</strong></p>
                                <p>📍 Locations created: <strong>{leadResults.locations_created || 0}</strong></p>
                              </div>
                              {leadResults.errors?.length > 0 && (
                                <div className="pt-1">
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-red-600 font-medium text-xs">✗ Errors: {leadResults.errors.length}</p>
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      className="h-6 text-xs px-2"
                                      onClick={() => downloadFailedRows(leadCsvData, leadResults.errors)}
                                    >
                                      <Download className="h-3 w-3 mr-1" />
                                      Download Failed Rows
                                    </Button>
                                  </div>
                                  <div className="p-2 bg-white border border-red-200 rounded max-h-40 overflow-y-auto">
                                    {leadResults.errors.map((e, i) => (
                                      <p key={i} className="text-xs text-red-700 leading-5">
                                        <span className="font-mono text-red-400">Row {e.row}:</span> {e.error}
                                      </p>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}

                <div className="flex justify-between gap-3 pt-3 border-t">
                  <Button variant="outline" onClick={() => setStep(1)}>
                    <ArrowLeft className="mr-2 h-4 w-4" />Back
                  </Button>
                  <Button onClick={handleClose} disabled={!leadResults}>
                    Finish
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
